import {useEffect,useMemo,useRef,useState} from 'react';
import {readPendingStoreCart,savePendingStoreCart,clearPendingStoreCart} from './PendingStoreCart';
import {readPendingMembership,savePendingMembership} from './PendingMembershipSelection';

type StoreItem={id:string;kind:'service'|'package'|'gift_card';code:string;name:string;
  priceCents:number;description:string;featured:boolean;position:number;
  recommendedAddOn:boolean;
  creditsPerUnit?:number;serviceCode?:string;sourceProductId?:string;
  giftCardTitle?:string;giftLegalText?:string;giftRecipientText?:string;};
type StoreMembership={id:string;name:string;description:string;
  firstPaymentCents:number;recurringPriceCents:number};
type StoreCatalog={services:StoreItem[];packages:StoreItem[];memberships:StoreMembership[];
  giftOptions:StoreItem[];messages:Record<string,string>};
type Gift={id:string;item:StoreItem;to:string;from:string;message:string};
type CartLine={item:StoreItem;quantity:number};
type Location={code:string;name:string;timezone:string;phone?:string|null};
type PublicQuote={currency:'USD';subtotalCents:number;totalsFinal:false;checkoutEnabled:false;
  lines:Array<{kind:'service'|'package'|'gift_card';id:string;name:string;quantity:number;
    unitPriceCents:number;lineSubtotalCents:number}>};
type QuoteStatus={status:'loading'|'ready'|'error';quote:PublicQuote|null};
function usePublicQuote(location:string,mode:'store'|'gift',lines:CartLine[]){
  const [state,setState]=useState<QuoteStatus>({status:'loading',quote:null});
  // A canonical string dependency prevents re-quoting on unrelated React
  // renders. Product prices in this payload are deliberately excluded.
  const lineKey=JSON.stringify(lines.map(line=>({
    kind:line.item.kind,id:line.item.id,quantity:line.quantity,
  })));
  useEffect(()=>{
    const controller=new AbortController();
    setState({status:'loading',quote:null});
    void fetch('/api/v1/shop/quote',{
      method:'POST',credentials:'same-origin',cache:'no-store',signal:controller.signal,
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({location,mode,lines:JSON.parse(lineKey) as unknown}),
    }).then(async response=>{
      if(!response.ok)throw Error('QUOTE_UNAVAILABLE');
      const result=await response.json() as {ok:boolean;data?:PublicQuote};
      if(!result.ok||!result.data||result.data.checkoutEnabled!==false)
        throw Error('QUOTE_UNAVAILABLE');
      if(!controller.signal.aborted)setState({status:'ready',quote:result.data});
    }).catch(()=>{if(!controller.signal.aborted)
      setState({status:'error',quote:null});});
    return ()=>controller.abort();
  },[location,mode,lineKey]);
  return state;
}
const price=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(cents/100);
function useCatalog(location:string){
  const [catalog,setCatalog]=useState<StoreCatalog|null>(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  useEffect(()=>{
    const controller=new AbortController();setLoading(true);setError('');setCatalog(null);
    void fetch('/api/v1/shop/catalog?'+new URLSearchParams({location}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal})
      .then(async response=>{
        if(!response.ok)throw Error('Catalog unavailable.');
        const result=await response.json() as {ok:boolean;data?:StoreCatalog};
        if(!result.ok||!result.data)throw Error('Catalog unavailable.');
        if(!controller.signal.aborted)setCatalog(result.data);
      }).catch(()=>{if(!controller.signal.aborted)setError('Store catalog is unavailable.');})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return ()=>controller.abort();
  },[location]);
  return {catalog,loading,error};
}
function CatalogCard({item,quantity,onChange}:{item:StoreItem;quantity:number;
  onChange:(value:number)=>void}){
  return <article className={'ecfs-public-product-card'+(item.featured?' is-featured':'')}>
    <div className="ecfs-public-product-copy">
      {item.featured&&<span className="ecfs-public-featured">Most Popular</span>}
      <h3>{item.name}</h3>
      {item.creditsPerUnit&&<p>Includes {item.creditsPerUnit} 60-minute float sessions.</p>}
      {item.description&&<p>{item.description}</p>}
    </div>
    <div className="ecfs-public-product-purchase">
      <strong>{price(item.priceCents)}</strong>
      <div className="ecfs-public-quantity" aria-label={'Quantity for '+item.name}>
        <button type="button" disabled={quantity===0} onClick={()=>onChange(Math.max(0,quantity-1))}
          aria-label={'Remove one '+item.name}>−</button>
        <output aria-label={'Selected quantity for '+item.name}>{quantity}</output>
        <button type="button" onClick={()=>onChange(Math.min(20,quantity+1))}
          aria-label={'Add one '+item.name}>+</button>
      </div>
    </div>
  </article>;
}

// Existing source membership prices are display-only until the actual
// membership lifecycle and recurring payments are implemented. Never put
// memberships into one-time service/package checkout or invent terms.
function MembershipCard({item,selected,onSelect}:{item:StoreMembership;
  selected:boolean;onSelect:()=>void}){
  return <article className={'ecfs-public-product-card ecfs-public-membership-card'+
    (selected?' is-selected':'')}>
    <label className="ecfs-public-membership-choice">
      <input type="radio" name="public-membership-choice"
        value={item.id} checked={selected} onChange={onSelect}/>
      <span className="ecfs-public-product-copy">
      <h3>{item.name}</h3>
      {item.description&&<p>{item.description}</p>}
      </span>
    </label>
    <div className="ecfs-public-membership-prices">
      <strong>{price(item.recurringPriceCents)} recurring</strong>
      {item.firstPaymentCents!==item.recurringPriceCents&&
        <small>{price(item.firstPaymentCents)} first payment</small>}
    </div>
  </article>;
}

type TestReadiness={testCheckoutApiEnabled:boolean};
type TestOrderResult={orderId:string;status:string;paid:boolean;fulfilled:boolean;
  subtotalCents:number};

function TestCheckoutReturn({isGuestAccount,onLogin}:{
  isGuestAccount:boolean;onLogin:()=>void;
}){
  const params=new URLSearchParams(window.location.search);
  const orderId=params.get('order')??'';
  const outcome=params.get('checkout');
  const [result,setResult]=useState<TestOrderResult|null>(null);
  const [error,setError]=useState('');
  const [checking,setChecking]=useState(true);
  useEffect(()=>{
    if(!['returned','cancelled'].includes(outcome??'')||
      !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(orderId)||
      !isGuestAccount){setChecking(false);return;}
    const controller=new AbortController();
    let interval:ReturnType<typeof setTimeout>|null=null,attempts=0;
    const read=async()=>{
      try{
        const response=await fetch('/api/v1/shop/stripe/test-orders/'+orderId,{
          credentials:'same-origin',cache:'no-store',signal:controller.signal,
        });
        if(!response.ok)throw Error('RECEIPT_UNAVAILABLE');
        const body=await response.json() as {ok:boolean;data?:TestOrderResult};
        if(!body.ok||body.data?.orderId!==orderId||
          typeof body.data.paid!=='boolean'||typeof body.data.fulfilled!=='boolean')
          throw Error('INVALID_RECEIPT');
        if(controller.signal.aborted)return;
        setResult(body.data);setError('');setChecking(false);
        if(!body.data.fulfilled&&attempts++<20)
          interval=setTimeout(()=>{void read();},3000);
      }catch{
        if(controller.signal.aborted)return;
        setChecking(false);setError('Payment status is temporarily unavailable.');
      }
    };
    void read();
    return ()=>{controller.abort();if(interval)clearTimeout(interval);};
  },[orderId,outcome,isGuestAccount]);
  if(!['returned','cancelled'].includes(outcome??''))return null;
  return <section className="ecfs-public-payment-return" role="region"
    aria-label="Test payment result">
    <h2>Payment status</h2>
    {!isGuestAccount&&<p>Sign in to view your payment status.
      <button type="button" onClick={onLogin}>Log In</button></p>}
    {checking&&<p role="status">Checking the verified payment record…</p>}
    {error&&<p role="alert">{error}</p>}
    {result?.fulfilled&&<p role="status">Stripe test payment completed.
      Your purchased service credits have been issued to your account.</p>}
    {result?.paid&&!result.fulfilled&&<p role="status">
      Stripe test payment was recorded. Credit fulfillment is still processing.
      Do not repurchase while this is being verified.</p>}
    {result&&!result.paid&&<p role="status">
      {outcome==='cancelled'
        ?'Checkout was cancelled. No verified payment has been recorded.'
        :'Returned from Stripe Checkout. Payment is not yet verified; no credits have been issued.'}
    </p>}
    {result&&<p>Order reference: {result.orderId}</p>}
  </section>;
}

function CartReview({lines,location,onBack,isGuestAccount,onLogin}:{
  lines:CartLine[];location:string;onBack:()=>void;
  isGuestAccount:boolean;onLogin:()=>void;
}){
  const {status,quote}=usePublicQuote(location,'store',lines);
  const [readiness,setReadiness]=useState<TestReadiness|null>(null);
  const [checkoutBusy,setCheckoutBusy]=useState(false);
  const [checkoutError,setCheckoutError]=useState('');
  const idempotencyKey=useRef(crypto.randomUUID());
  useEffect(()=>{
    const controller=new AbortController();
    void fetch('/api/v1/shop/stripe/readiness',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('NOT_ENABLED');
      const body=await response.json() as {ok:boolean;data?:TestReadiness};
      if(body.ok&&body.data&&!controller.signal.aborted)setReadiness(body.data);
    }).catch(()=>{if(!controller.signal.aborted)setReadiness(null);});
    return ()=>controller.abort();
  },[]);
  const canPurchase=lines.length>0&&lines.every(x=>
    typeof x.item.sourceProductId==='string'&&
    /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(x.item.sourceProductId));
  const loginWithCart=()=>{
    // Gift Cards are never eligible for the one-time Store cart.
    const storeItems=lines.flatMap(({item,quantity})=>
      item.kind==='gift_card'?[]:[{kind:item.kind,id:item.id,quantity}]);
    savePendingStoreCart(location,storeItems);
    onLogin();
  };
  const startTestCheckout=async()=>{
    if(checkoutBusy||status!=='ready'||!quote||!canPurchase||
      !isGuestAccount||!readiness?.testCheckoutApiEnabled)return;
    setCheckoutBusy(true);setCheckoutError('');
    try{
      const response=await fetch('/api/v1/shop/stripe/test-checkout',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          location,sourceTag:'2026-10-29',
          idempotencyKey:idempotencyKey.current,
          lines:lines.map(x=>({productId:x.item.sourceProductId,
            quantity:x.quantity})),
        }),
      });
      if(!response.ok){
        if(response.status===409)throw Error('Items or prices changed. Review your cart.');
        if(response.status===401)throw Error('Sign in to complete checkout.');
        throw Error('Secure test checkout is temporarily unavailable.');
      }
      const body=await response.json() as {ok:boolean;data?:{
        orderId:string;checkoutUrl:string;paymentStatus:string}};
      if(!body.ok||body.data?.paymentStatus!=='UNPAID_TEST_SESSION'||
        !body.data.checkoutUrl)throw Error('Invalid secure checkout response.');
      const destination=new URL(body.data.checkoutUrl);
      if(destination.protocol!=='https:'||
        destination.hostname!=='checkout.stripe.com')
        throw Error('Invalid secure checkout destination.');
      // Only Stripe hosts payment details. This redirects to a TEST-mode
      // Session; credit issuance requires the later signed Stripe webhook.
      window.location.assign(destination.href);
    }catch(err){
      setCheckoutError(err instanceof Error?err.message:
        'Secure test checkout is temporarily unavailable.');
      setCheckoutBusy(false);
    }
  };
  return <section className="ecfs-public-cart-review" aria-label="Review your order">
    <h2>Review your order</h2>
    {status==='loading'&&<p role="status">Checking current Store prices…</p>}
    {status==='error'&&<p role="alert">Current prices could not be verified.
      Please return to the Store and review your cart.</p>}
    {status==='ready'&&quote&&<>
      {quote.lines.map(line=><p key={line.kind+line.id}>
        {line.quantity} × {line.name} <strong>{price(line.lineSubtotalCents)}</strong>
      </p>)}
      <p className="ecfs-public-total">Subtotal <strong>{price(quote.subtotalCents)}</strong></p>
      <small>Taxes and discounts, if applicable, have not been calculated.</small>
      {canPurchase&&readiness?.testCheckoutApiEnabled
        ?isGuestAccount
          ?<button type="button" disabled={checkoutBusy}
            onClick={()=>void startTestCheckout()}>
            {checkoutBusy?'Opening Stripe Checkout…':'Continue to Stripe Test Checkout'}
          </button>
          :<button type="button" onClick={loginWithCart}>Log In to Checkout</button>
        :<p role="status">Online payment is not yet available for these items.
          No purchase has been made.</p>}
    </>}
    {checkoutError&&<p role="alert">{checkoutError}</p>}
    <button type="button" onClick={onBack}>Continue Shopping</button>
  </section>;
}
function MembershipTestReturn({isGuestAccount}:{isGuestAccount:boolean}){
  const params=new URLSearchParams(window.location.search);
  const enrollment=params.get('enrollment')??'';
  const outcome=params.get('membershipCheckout');
  const valid=/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(enrollment);
  const [status,setStatus]=useState('');
  useEffect(()=>{
    if(!outcome||!valid||!isGuestAccount)return;
    let active=true;
    const poll=async()=>{
      try{
        const response=await fetch('/api/v1/shop/stripe/test-membership/'+
          encodeURIComponent(enrollment),{credentials:'same-origin',cache:'no-store'});
        const body=await response.json() as {ok:boolean;data?:{
          status:string;paidInvoiceCount:number}};
        if(!response.ok||!body.ok||!body.data)
          throw Error('Membership status could not be verified.');
        if(active)setStatus(body.data.status==='ACTIVE'&&
          body.data.paidInvoiceCount>=1
          ?'Membership active. Stripe TEST invoice paid and service credits issued.'
          :body.data.status==='ENDED'?'Membership ended.':
          'Membership payment has not yet been verified.');
      }catch{if(active)setStatus('Membership payment verification is unavailable.');}
    };
    void poll();const interval=window.setInterval(()=>void poll(),3000);
    return ()=>{active=false;window.clearInterval(interval);};
  },[outcome,valid,enrollment,isGuestAccount]);
  if(!outcome||!valid)return null;
  return <p role="status">{!isGuestAccount?'Sign in to view your membership payment.':
    status||'Checking signed Stripe TEST membership invoice…'}</p>;
}
export function PublicStoreWorkspace({location,isGuestAccount=false,onLogin=()=>{}}:{
  location:Location;isGuestAccount?:boolean;onLogin?:()=>void;
}){
  const {catalog,loading,error}=useCatalog(location.code);
  const [counts,setCounts]=useState<Record<string,number>>({});
  const [closed,setClosed]=useState<Record<string,boolean>>({});
  const [review,setReview]=useState(false);
  const [selectedMembershipId,setSelectedMembershipId]=useState<string|null>(null);
  const [membershipBusy,setMembershipBusy]=useState(false);
  const [membershipError,setMembershipError]=useState('');
  const memberCheckoutKey=useRef(crypto.randomUUID());
  useEffect(()=>{setCounts({});setClosed({});setReview(false);
    setSelectedMembershipId(null);memberCheckoutKey.current=crypto.randomUUID();
    setMembershipError('');},[location.code]);
  useEffect(()=>{
    if(!catalog)return;
    const pending=readPendingStoreCart();
    if(!pending||pending.location!==location.code)return;
    const available=[...catalog.services,...catalog.packages];
    const valid=pending.items.every(line=>
      available.some(item=>item.kind===line.kind&&item.id===line.id));
    if(valid){
      const restored:Record<string,number>={};
      for(const item of pending.items)restored[item.kind+':'+item.id]=item.quantity;
      setCounts(restored);setReview(true);
    }
    clearPendingStoreCart();
  },[catalog,location.code]);
  useEffect(()=>{
    if(!catalog||!isGuestAccount)return;
    const id=readPendingMembership(location.code);
    if(id&&catalog.memberships.some(x=>x.id===id))setSelectedMembershipId(id);
  },[catalog,location.code,isGuestAccount]);
  const quantity=(item:StoreItem)=>counts[item.kind+':'+item.id]??0;
  const change=(item:StoreItem,value:number)=>setCounts(old=>({
    ...old,[item.kind+':'+item.id]:value}));
  const selectedMembership=catalog?.memberships.find(
    item=>item.id===selectedMembershipId);
  const startMemberCheckout=async()=>{
    if(!selectedMembership||membershipBusy)return;
    if(!isGuestAccount){savePendingMembership(location.code,selectedMembership.id);
      onLogin();return;}
    setMembershipBusy(true);setMembershipError('');
    try{
      const response=await fetch('/api/v1/shop/stripe/test-membership-checkout',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,
          productId:selectedMembership.id,
          idempotencyKey:memberCheckoutKey.current}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        checkoutUrl:string;enrollmentId:string};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data?.checkoutUrl)
        throw Error(body.error?.code??'Membership TEST checkout unavailable.');
      const destination=new URL(body.data.checkoutUrl);
      if(destination.protocol!=='https:'||
        destination.hostname!=='checkout.stripe.com')
        throw Error('Invalid Stripe membership checkout destination.');
      window.location.assign(destination.href);
    }catch(e){setMembershipError(e instanceof Error?e.message:
      'Membership TEST checkout unavailable.');setMembershipBusy(false);}
  };
  const all=[...(catalog?.services??[]),...(catalog?.packages??[])];
  const lines=all.filter(item=>quantity(item)>0)
    .map(item=>({item,quantity:quantity(item)}));
  const total=lines.reduce((n,line)=>n+line.item.priceCents*line.quantity,0);
  const suggestion=useMemo(()=>{
    if(!catalog)return null;
    const selectedIds=new Set(lines.map(l=>l.item.id));
    const choices=[...catalog.packages,...catalog.services].filter(item=>
      !selectedIds.has(item.id));
    // Location Settings can explicitly select recommended add-ons. Preserve
    // the existing fallback until each location configures its recommendations.
    return choices.find(item=>item.recommendedAddOn)??choices[0]??null;
  },[catalog,counts]);
  if(loading)return <section className="ecfs-public-content"><p role="status">
    Loading Store…</p></section>;
  if(error||!catalog)return <section className="ecfs-public-content">
    <p role="alert">{error||'Store unavailable.'}</p></section>;
  const sections:[
    {key:'memberships';label:string;items:StoreMembership[];help:string},
    ...Array<{key:'packages'|'services';label:string;items:StoreItem[];help:string}>
  ]=[
    {key:'memberships',label:'Memberships',items:catalog.memberships,
      help:catalog.messages.membershipsSectionMessage},
    {key:'packages',label:'Packages',items:catalog.packages,
      help:catalog.messages.packagesSectionMessage},
    {key:'services',label:'Services',items:catalog.services,
      help:catalog.messages.servicesSectionMessage},
  ];
  return <section className="ecfs-public-content ecfs-public-commerce"
    aria-label="Public Store">
    {location.phone&&<p className="ecfs-public-support">Questions? Call/text us at
      {' '}<a href={'tel:'+location.phone}>{location.phone}</a></p>}
    {catalog.messages.storeHelpMessage&&<p>{catalog.messages.storeHelpMessage}</p>}
    <h1>Store</h1>
    <TestCheckoutReturn isGuestAccount={isGuestAccount} onLogin={onLogin}/>
    <MembershipTestReturn isGuestAccount={isGuestAccount}/>
    {review?<CartReview lines={lines} location={location.code}
      isGuestAccount={isGuestAccount} onLogin={onLogin}
      onBack={()=>setReview(false)}/>:
      <>
        {sections.map(section=><section className="ecfs-public-category" key={section.key}>
          <button type="button" className="ecfs-public-category-toggle"
            aria-expanded={!closed[section.key]}
            onClick={()=>setClosed(old=>({...old,[section.key]:!old[section.key]}))}>
            <span>{section.label}</span><span>{closed[section.key]?'＋':'−'}</span>
          </button>
          {!closed[section.key]&&<div className="ecfs-public-category-body">
            {section.help&&<p>{section.help}</p>}
            {section.key==='memberships'?
              section.items.length>0?<>
                {section.items.map(item=><MembershipCard key={item.id} item={item}
                  selected={selectedMembershipId===item.id}
                  onSelect={()=>{setSelectedMembershipId(item.id);
                     memberCheckoutKey.current=crypto.randomUUID();
                     setMembershipError('');}}/>)}
                {selectedMembership&&<div className="ecfs-public-selected-membership"
                  role="status" aria-label="Selected membership">
                  <strong>Selected: {selectedMembership.name}</strong>
                  <p>First payment: {price(selectedMembership.firstPaymentCents)}</p>
                  <p>Recurring payment: {price(selectedMembership.recurringPriceCents)}</p>
                </div>}
                {selectedMembership&&<div className="ecfs-public-membership-purchase-status">
                  <button type="button" disabled={membershipBusy}
                    onClick={()=>void startMemberCheckout()}>
                    {membershipBusy?'Opening Stripe TEST Checkout…':
                      isGuestAccount?'Continue to Stripe TEST Checkout':'Log In to Checkout'}
                  </button>
                  {membershipError&&<p role="alert">{membershipError}</p>}
                </div>}
              </>:<p className="ecfs-public-no-products">
                No memberships are publicly listed for this location.
              </p>
              :section.items.length>0?
                section.items.map(item=><CatalogCard key={item.id}
                  item={item} quantity={quantity(item)} onChange={n=>change(item,n)}/>):
                <p className="ecfs-public-no-products">
                  No online items are currently offered in this section.
                </p>}
          </div>}
        </section>)}
        {lines.length>0&&<section className="ecfs-public-addons" aria-label="You might also like">
          <h2>Before you check out, you might also like…</h2>
          {suggestion?<CatalogCard item={suggestion} quantity={quantity(suggestion)}
            onChange={n=>change(suggestion,n)}/>:
            <p>All currently listed items are already in your cart.</p>}
        </section>}
        <div className="ecfs-public-cart-footer">
          <span>{lines.reduce((n,l)=>n+l.quantity,0)} items</span>
          <strong>Estimated Subtotal {price(total)}</strong>
          <button type="button" disabled={!lines.length} onClick={()=>setReview(true)}>
            Review Cart</button>
        </div>
      </>}
  </section>;
}

type GiftDeliveryChoice={target:'buyer'|'recipient';recipientEmail:string;
  mode:'immediate'|'scheduled';scheduledAtLocal:string};
const defaultGiftDelivery=():GiftDeliveryChoice=>({
  target:'buyer',recipientEmail:'',mode:'immediate',scheduledAtLocal:'',
});
function GiftTestCheckoutReturn(){
  const params=new URLSearchParams(window.location.search);
  const draftId=params.get('gift_draft');
  const outcome=params.get('checkout');
  const [status,setStatus]=useState('');
  useEffect(()=>{
    if(!draftId||!outcome||!/^[a-f0-9-]{36}$/i.test(draftId))return;
    if(outcome==='cancelled'){setStatus('Stripe Test Checkout was cancelled. No gift was purchased.');return;}
    const controller=new AbortController();
    void fetch('/api/v1/shop/stripe/test-gift-drafts/'+encodeURIComponent(draftId),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(response.status===401)throw Error('Sign in to review gift delivery.');
      if(!response.ok)throw Error('Gift status is not available yet.');
      const body=await response.json() as {ok:boolean;data?:{status:string}};
      if(!body.ok||!body.data)throw Error('Gift status is not available yet.');
      if(!controller.signal.aborted)setStatus(body.data.status==='ISSUED_PENDING_DELIVERY'?
        'Payment verified. Your test gift is queued for delivery. Check the recipient email.':
        body.data.status==='PAID_UNFULFILLED'?
          'Test payment verified. Gift processing is still in progress.':
          'Test gift checkout status: '+body.data.status);
    }).catch(e=>{if(!controller.signal.aborted)setStatus(e instanceof Error?
      e.message:'Unable to verify gift status.');});
    return ()=>controller.abort();
  },[draftId,outcome]);
  return !draftId||!outcome?null:<p role="status">{status||
    'Checking verified gift payment status…'}</p>;
}
type PurchasedTestGift={
  voucherId:string;draftId:string;title:string;recipientName:string;
  destinationEmail:string;deliveryDueAt:string;purchasedAt:string;
  deliveryStatus:string;redeemed:boolean;refunded:boolean;
  giftNumber:string|null;printableHtml:string|null;
  resendCount:number;lastResendStatus:string|null;
};
function GiftAccountPanel({location,isGuestAccount,onLogin}:{
  location:string;isGuestAccount:boolean;onLogin:()=>void;
}){
  const [giftNumber,setGiftNumber]=useState('');
  const [claimBusy,setClaimBusy]=useState(false);
  const [claimResult,setClaimResult]=useState('');
  const [history,setHistory]=useState<PurchasedTestGift[]>([]);
  const [historyStatus,setHistoryStatus]=useState('Loading Gift Card history…');
  const [resendBusy,setResendBusy]=useState('');
  const [resendMessages,setResendMessages]=useState<Record<string,string>>({});
  const [reloadHistory,setReloadHistory]=useState(0);
  useEffect(()=>{
    if(!isGuestAccount){setHistory([]);setHistoryStatus('');return;}
    const controller=new AbortController();
    setHistoryStatus('Loading Gift Card history…');
    void fetch('/api/v1/guest/gifts/purchased',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Gift purchase history unavailable.');
      const result=await response.json() as {ok:boolean;data?:PurchasedTestGift[]};
      if(!result.ok||!Array.isArray(result.data))throw Error('History unavailable.');
      if(!controller.signal.aborted){
        setHistory(result.data);setHistoryStatus('');
      }
    }).catch(()=>{
      if(!controller.signal.aborted)setHistoryStatus(
        'Gift purchase history is unavailable right now.');
    });
    return ()=>controller.abort();
  },[isGuestAccount,reloadHistory]);
  const redeem=async()=>{
    if(!isGuestAccount||claimBusy)return;
    const number=giftNumber.trim().toLowerCase();
    if(!/^[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12}\.[a-f0-9]{64}$/.test(number)){
      setClaimResult('Enter the complete Gift Card Number from your email.');
      return;
    }
    setClaimBusy(true);setClaimResult('');
    try{
      const response=await fetch('/api/v1/guest/gifts/redeem',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({giftNumber:number,location}),
      });
      const result=await response.json() as {ok:boolean;data?:{
        originalUses:number;replayed:boolean};error?:{code:string}};
      if(!response.ok||!result.ok||!result.data)
        throw Error(response.status===401?'Sign in to redeem this gift.':
          'This Gift Card cannot be redeemed. Check its number and status.');
      setClaimResult(result.data.replayed?
        'This gift was already redeemed to your Service Credits.':
        'Gift redeemed! '+result.data.originalUses+
        ' service credit use(s) added to your account.');
      setReloadHistory(n=>n+1);
    }catch(e){
      setClaimResult(e instanceof Error?e.message:'Unable to redeem gift.');
    }finally{setClaimBusy(false);}
  };
  const resendGift=async(gift:PurchasedTestGift)=>{
    if(!isGuestAccount||resendBusy)return;
    setResendBusy(gift.voucherId);
    setResendMessages(old=>({...old,[gift.voucherId]:''}));
    try{
      const response=await fetch('/api/v1/guest/gifts/'+
        encodeURIComponent(gift.voucherId)+'/resend',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({requestKey:crypto.randomUUID()}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        status:string;destinationEmail:string};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.code==='TEST_GIFT_RESEND_IN_PROGRESS'?
          'A resend is already pending or requires review.':
          'This Gift Card cannot be resent right now.');
      setResendMessages(old=>({...old,[gift.voucherId]:
        body.data?.status==='ACCEPTED'?'Gift Card email already accepted.':
          'Gift Card resend queued to '+body.data?.destinationEmail+
          '. Email delivery has not yet been confirmed.'}));
      setReloadHistory(n=>n+1);
    }catch(e){
      setResendMessages(old=>({...old,[gift.voucherId]:
        e instanceof Error?e.message:'Gift Card resend unavailable.'}));
    }finally{setResendBusy('');}
  };
  const printGift=(gift:PurchasedTestGift)=>{
    if(!gift.printableHtml)return;
    const url=URL.createObjectURL(new Blob([gift.printableHtml],{
      type:'text/html;charset=utf-8'}));
    const a=document.createElement('a');
    a.href=url;a.download='ECFS-Gift-Card.html';
    document.body.appendChild(a);a.click();a.remove();
    window.setTimeout(()=>URL.revokeObjectURL(url),1000);
  };
  return <section className="ecfs-public-gift-account" aria-label="Redeem a Gift Card">
    <h2>Redeem a Gift Card</h2>
    {isGuestAccount?<form onSubmit={e=>{e.preventDefault();void redeem();}}>
      <label>Gift Card Number
        <input aria-label="Gift Card Number" value={giftNumber}
          onChange={e=>{setGiftNumber(e.target.value);setClaimResult('');}}
          placeholder="Enter the full number from your gift email"
          autoComplete="off" spellCheck={false} required/>
      </label>
      <button type="submit" disabled={claimBusy}>
        {claimBusy?'Redeeming…':'Redeem Gift Card'}</button>
    </form>:<button type="button" onClick={onLogin}>
      Sign In to Redeem</button>}
    {claimResult&&<p role="status">{claimResult}</p>}
    {isGuestAccount&&<section aria-label="My Purchased Gift Cards">
      <h2>My Purchased Gift Cards</h2>
      {historyStatus&&<p role="status">{historyStatus}</p>}
      {!historyStatus&&history.length===0&&
        <p>No paid test Gift Cards are listed for this account.</p>}
      {history.map(g=><div key={g.voucherId}
          className="ecfs-public-gift-delivery">
        <strong>{g.title}</strong>
        <p>Purchased {new Date(g.purchasedAt).toLocaleDateString()}</p>
        <p>Sent to {g.destinationEmail||'delivery pending'}.
          {' '}{g.refunded?'Refunded':g.redeemed?'Redeemed':
            g.deliveryStatus==='ACCEPTED'?'Email accepted by provider':
            g.deliveryStatus==='REVIEW_REQUIRED'?'Delivery requires review':
            g.deliveryStatus==='PENDING_SENDER'?'Waiting for delivery':
            'Delivery in progress'}</p>
        {g.giftNumber&&<details><summary>Gift Card Number / Print</summary>
          <p style={{overflowWrap:'anywhere'}}>{g.giftNumber}</p>
          <button type="button" onClick={()=>printGift(g)}>Print Gift Card</button>
        </details>}
        {g.resendCount>0&&<p>Resend requests: {g.resendCount}.
          {' '}Latest: {g.lastResendStatus==='ACCEPTED'?
            'Accepted by email provider':g.lastResendStatus==='REVIEW_REQUIRED'?
            'Requires review':g.lastResendStatus==='SENDING'?
            'Sending':'Queued'}</p>}
        {g.giftNumber&&!g.refunded&&
          <button type="button" disabled={Boolean(resendBusy)||
            ['PENDING_SENDER','SENDING','REVIEW_REQUIRED']
              .includes(g.lastResendStatus??'')}
            onClick={()=>void resendGift(g)}>
            {resendBusy===g.voucherId?'Requesting Resend…':'Resend Gift Card'}
          </button>}
        {resendMessages[g.voucherId]&&<p role="status">
          {resendMessages[g.voucherId]}</p>}
      </div>)}
      <button type="button" onClick={()=>setReloadHistory(n=>n+1)}>
        Refresh Gift Card History</button>
    </section>}
  </section>;
}
function GiftQuoteReview({gifts,location,onBack,isGuestAccount,onLogin}:{
  gifts:Gift[];location:string;onBack:()=>void;
  isGuestAccount:boolean;onLogin:()=>void;
}){
  const aggregated=new Map<string,CartLine>();
  for(const gift of gifts){
    const key=gift.item.kind+':'+gift.item.id;
    const existing=aggregated.get(key);
    aggregated.set(key,{item:gift.item,quantity:(existing?.quantity??0)+1});
  }
  const lines=[...aggregated.values()];
  const {status,quote}=usePublicQuote(location,'gift',lines);
  const quoted=(gift:Gift)=>quote?.lines.find(row=>
    row.kind===gift.item.kind&&row.id===gift.item.id);
  const [readiness,setReadiness]=useState<'loading'|'ready'|'unavailable'>('loading');
  const [delivery,setDelivery]=useState<Record<string,GiftDeliveryChoice>>({});
  const [busy,setBusy]=useState(false);
  const [checkoutError,setCheckoutError]=useState('');
  const idempotency=useRef(crypto.randomUUID());
  useEffect(()=>{
    const controller=new AbortController();
    void fetch('/api/v1/shop/stripe/readiness',{
      cache:'no-store',credentials:'same-origin',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Unavailable');
      const result=await response.json() as {ok:boolean;data?:{
        giftTestCheckoutEnabled:boolean}};
      if(!controller.signal.aborted)setReadiness(result.ok&&
        result.data?.giftTestCheckoutEnabled===true?'ready':'unavailable');
    }).catch(()=>{if(!controller.signal.aborted)setReadiness('unavailable');});
    return ()=>controller.abort();
  },[]);
  const getDelivery=(id:string)=>delivery[id]??defaultGiftDelivery();
  const changeDelivery=(id:string,fields:Partial<GiftDeliveryChoice>)=>{
    setDelivery(old=>({...old,[id]:{...getDelivery(id),...fields}}));
    setCheckoutError('');
  };
  const startCheckout=async()=>{
    if(busy||!isGuestAccount||readiness!=='ready'||status!=='ready'||!quote)return;
    setBusy(true);setCheckoutError('');
    try{
      const prepared=gifts.map(g=>{
        const config=getDelivery(g.id);
        if(config.target==='recipient'&&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(config.recipientEmail.trim()))
          throw Error('Enter a valid recipient email address for each recipient delivery.');
        let scheduledAt:string|undefined;
        if(config.mode==='scheduled'){
          const at=Date.parse(config.scheduledAtLocal);
          if(!Number.isFinite(at)||at<=Date.now())
            throw Error('Choose a future delivery date and time.');
          scheduledAt=new Date(at).toISOString();
        }
        return {productId:g.item.id,to:g.to,from:g.from,message:g.message,
          delivery:{target:config.target,
            ...(config.target==='recipient'?{
              recipientEmail:config.recipientEmail.trim()}:{}),
            mode:config.mode,...(scheduledAt?{scheduledAt}:{})}};
      });
      const response=await fetch('/api/v1/shop/stripe/test-gift-checkout',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location,idempotencyKey:idempotency.current,gifts:prepared}),
      });
      const result=await response.json() as {ok:boolean;data?:{
        checkoutUrl:string;draftId:string};error?:{code:string}};
      if(!response.ok||!result.ok||!result.data?.checkoutUrl)
        throw Error(result.error?.code==='REVIEW_TEST_GIFT_DRAFT'?
          'A gift selection changed. Review it again before checkout.':
          'Unable to create Stripe TEST gift checkout.');
      const url=new URL(result.data.checkoutUrl);
      if(url.protocol!=='https:'||url.hostname!=='checkout.stripe.com')
        throw Error('Invalid Stripe checkout destination.');
      window.location.assign(url.href);
    }catch(e){
      setCheckoutError(e instanceof Error?e.message:'Unable to start checkout.');
      setBusy(false);
    }
  };
  return <section className="ecfs-public-cart-review" aria-label="Gift Card Order Review">
    <h2>Review your Gift Cards</h2>
    {status==='loading'&&<p role="status">Checking current gift prices…</p>}
    {status==='error'&&<p role="alert">Gift prices could not be verified.
      Please edit your gift selection.</p>}
    {status==='ready'&&quote&&<>
      {gifts.map(g=><div key={g.id} className="ecfs-public-gift-delivery">
        <p>{quoted(g)?.name??g.item.name}
          {g.to?' for '+g.to:''}
          <strong>{price(quoted(g)?.unitPriceCents??0)}</strong></p>
        {readiness==='ready'&&<>
          <fieldset><legend>Email gift to</legend>
            <label><input type="radio" name={'target-'+g.id}
              checked={getDelivery(g.id).target==='buyer'}
              onChange={()=>changeDelivery(g.id,{target:'buyer'})}/>Buyer</label>
            <label><input type="radio" name={'target-'+g.id}
              checked={getDelivery(g.id).target==='recipient'}
              onChange={()=>changeDelivery(g.id,{target:'recipient'})}/>Recipient</label>
            {getDelivery(g.id).target==='recipient'&&<label>
              Recipient Email <input type="email"
                value={getDelivery(g.id).recipientEmail}
                onChange={e=>changeDelivery(g.id,{recipientEmail:e.target.value})}
                placeholder="recipient@example.com" required/></label>}
          </fieldset>
          <fieldset><legend>Delivery time</legend>
            <label><input type="radio" name={'mode-'+g.id}
              checked={getDelivery(g.id).mode==='immediate'}
              onChange={()=>changeDelivery(g.id,{mode:'immediate'})}/>Send now</label>
            <label><input type="radio" name={'mode-'+g.id}
              checked={getDelivery(g.id).mode==='scheduled'}
              onChange={()=>changeDelivery(g.id,{mode:'scheduled'})}/>Send later</label>
            {getDelivery(g.id).mode==='scheduled'&&<label>
              Delivery date and time
              <input type="datetime-local" value={getDelivery(g.id).scheduledAtLocal}
                onChange={e=>changeDelivery(g.id,{scheduledAtLocal:e.target.value})}
                required/></label>}
          </fieldset>
        </>}
      </div>)}
      <p className="ecfs-public-total">Subtotal
        <strong>{price(quote.subtotalCents)}</strong></p>
      <small>Taxes and discounts, if applicable, have not been calculated.</small>
      {readiness==='ready'
        ?isGuestAccount
          ?<button type="button" disabled={busy} onClick={()=>void startCheckout()}>
            {busy?'Opening Stripe Checkout…':'Continue to Stripe Test Checkout'}</button>
          :<button type="button" onClick={onLogin}>Sign In to Checkout</button>
        :<p role="status">Gift card payment and delivery are not yet connected.
          No gift card has been purchased or sent.</p>}
    </>}
    {checkoutError&&<p role="alert">{checkoutError}</p>}
    <button type="button" onClick={onBack} disabled={busy}>Edit Gift Cards</button>
  </section>;
}

export function PublicGiftCardsWorkspace({location,isGuestAccount=false,onLogin=()=>{}}:{
  location:Location;isGuestAccount?:boolean;onLogin?:()=>void;
}){
  const {catalog,loading,error}=useCatalog(location.code);
  const [selectedId,setSelectedId]=useState('');
  const [to,setTo]=useState('');
  const [from,setFrom]=useState('');
  const [message,setMessage]=useState('');
  const [gifts,setGifts]=useState<Gift[]>([]);
  const [review,setReview]=useState(false);
  useEffect(()=>{setSelectedId('');setGifts([]);setReview(false);setTo('');setFrom('');
    setMessage('');},[location.code]);
  const options=catalog?.giftOptions??[];
  // Once a gift is added, the next gift is an empty draft until a choice is
  // made. Never silently count the default option as another purchased gift.
  const current=options.find(item=>item.id===selectedId)??
    (gifts.length===0?options[0]:undefined);
  const total=gifts.reduce((n,g)=>n+g.item.priceCents,0)+(current?.priceCents??0);
  const addGift=()=>{
    if(!current)return;
    setGifts(old=>[...old,{id:crypto.randomUUID(),item:current,to,from,message}]);
    setTo('');setFrom('');setMessage('');setSelectedId('');
  };
  const assembled=current?[...gifts,{id:'current',item:current,to,from,message}]:gifts;
  return <section className="ecfs-public-content ecfs-public-gifts" aria-label="Gift Cards">
    <GiftTestCheckoutReturn/>
    <header>
      <h1>🎁 Give the Gift of Total Relaxation for Mind &amp; Body!</h1>
      <p>✨ From stress and pain relief to deep sleep, this gift makes a difference.</p>
      <p>📍 Physical gift cards available onsite</p>
      <p>🌐 Usable at all locations</p>
      {location.phone&&<p>Questions? Call/text <a href={'tel:'+location.phone}>
        {location.phone}</a></p>}
      {catalog?.messages.giftCardStoreHelpMessage&&
        <p>{catalog.messages.giftCardStoreHelpMessage}</p>}
    </header>
    <GiftAccountPanel location={location.code} isGuestAccount={isGuestAccount}
      onLogin={onLogin}/>
    {loading&&<p role="status">Loading gift options…</p>}
    {error&&<p role="alert">{error}</p>}
    {!loading&&!error&&(!options.length?
      <p>No gift card options are currently available.</p>:
      review?
        <GiftQuoteReview gifts={assembled} location={location.code}
          isGuestAccount={isGuestAccount} onLogin={onLogin}
          onBack={()=>setReview(false)}/>:
        <>
          <fieldset className="ecfs-public-gift-options">
            <legend>Create your Gift Cards</legend>
            {options.map(item=><label key={item.kind+item.id}>
              <input type="radio" name="gift-choice"
                checked={current?.id===item.id}
                onChange={()=>setSelectedId(item.id)}/>
              <span>{item.name}</span><strong>{price(item.priceCents)}</strong>
            </label>)}
          </fieldset>
          {current&&<div className="ecfs-public-gift-preview">
            <div className="ecfs-public-gift-brand">
              <strong>East Coast Float Spa</strong>
              <h2>{current.giftCardTitle||current.name}</h2>
              {current.giftRecipientText&&<p>{current.giftRecipientText}</p>}
              {current.giftLegalText&&<p>{current.giftLegalText}</p>}
            </div>
            <div className="ecfs-public-gift-message-fields">
              <label>To (optional)<input value={to} onChange={e=>setTo(e.target.value)}
                placeholder="Who's it for? (optional)" maxLength={120}/></label>
              <label>From (optional)<input value={from} onChange={e=>setFrom(e.target.value)}
                placeholder="Your Name (optional)" maxLength={120}/></label>
              <label>Personal Message (optional)
                <textarea value={message} onChange={e=>setMessage(e.target.value)}
                  placeholder="Write a Personal Message (optional)" maxLength={600}
                  rows={3}/></label>
            </div>
            <footer>A printable gift card will be emailed to you.
              Delivery options will be available during checkout.</footer>
          </div>}
          {gifts.length>0&&<section aria-label="Additional Gift Cards">
            <h2>Additional Gift Cards</h2>
            {gifts.map(g=><div className="ecfs-public-extra-gift" key={g.id}>
              <span>{g.item.name}{g.to?' for '+g.to:''}</span>
              <strong>{price(g.item.priceCents)}</strong>
              <button type="button" onClick={()=>setGifts(items=>
                items.filter(x=>x.id!==g.id))} aria-label={'Remove gift '+g.item.name}>
                Remove</button>
            </div>)}
          </section>}
          <div className="ecfs-public-cart-footer">
            <strong>Estimated Subtotal: {price(total)}</strong>
            <button type="button" disabled={!current}
              onClick={addGift}>+ Add Another Gift Card</button>
            <button type="button" onClick={()=>setReview(true)}>Review Gift Cards</button>
          </div>
        </>)}
  </section>;
}
